import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import lifecycleService from '../../../services/lifecycleService';

export default function OnboardingChecklistModal({ employee, isOpen, onClose, onUpdated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !employee?.id) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await lifecycleService.getOnboarding(employee.id);
      if (result.success) {
        setItems(result.data || []);
      } else {
        setError(result.error || 'Failed to load checklist');
      }
      setLoading(false);
    };
    load();
  }, [isOpen, employee?.id]);

  const toggleItem = async (item) => {
    setSavingKey(item.item_key);
    const result = await lifecycleService.updateOnboardingItem(employee.id, {
      item_key: item.item_key,
      is_completed: !item.is_completed,
    });
    setSavingKey(null);
    if (result.success) {
      setItems(result.data || []);
      onUpdated?.();
    } else {
      setError(result.error || 'Failed to update item');
    }
  };

  if (!isOpen) return null;

  const completed = items.filter((i) => i.is_completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Onboarding Checklist</h2>
            <p className="text-sm text-gray-500">
              {employee?.full_name}
              {employee?.employee_code ? ` · ${employee.employee_code}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">{completed}/{total} ({pct}%)</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <p className="text-gray-500 text-sm">Loading checklist…</p>}
          {error && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
          )}
          {!loading && items.length === 0 && (
            <p className="text-gray-500 text-sm">No onboarding items found for this employee.</p>
          )}
          <ul className="space-y-2">
            {items.map((item) => (
              <li
                key={item.id || item.item_key}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  item.is_completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <button
                  type="button"
                  disabled={savingKey === item.item_key}
                  onClick={() => toggleItem(item)}
                  className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${
                    item.is_completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {item.is_completed && <Icon name="Check" size={12} color="white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${item.is_completed ? 'text-green-800 line-through' : 'text-gray-900'}`}>
                    {item.item_label || item.item_key}
                  </p>
                  {item.completed_at && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Completed {new Date(item.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
